import { numVal } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { Label } from '@rn-primitives/dropdown-menu'
import { useState } from 'react'
import Animated, { LinearTransition } from 'react-native-reanimated'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { PriceInput } from '~/components/ui/PriceInput'
import { cn } from '~/lib/utils'

export function NumberRangeInput({
  label,
  showMin: showMinInitial = true,
  showMax: showMaxInitial = true,
  min,
  max,
  onChange,
  minPlaceholder,
  maxPlaceholder,
  minProps,
  maxProps,
  formatted = false,
  unit,
  minPrefix,
  maxPrefix,
  inputClassName,
  type = 'number',
  testIdBase,
  className,
}: {
  label: string
  showMin?: boolean
  showMax?: boolean
  min?: number
  max?: number
  onChange: (next: { min?: number; max?: number }) => void
  minPlaceholder?: string
  maxPlaceholder?: string
  minProps?: { step?: number; min?: number; max?: number }
  maxProps?: { step?: number; min?: number; max?: number }
  formatted?: boolean
  unit?: string
  minPrefix?: string
  maxPrefix?: string
  className?: string
  inputClassName?: string
  type?: 'number' | 'price'
  testIdBase?: string
}) {
  const { t } = useTranslations('common')
  const { t: tValidation } = useTranslations('validation')

  const [showMin, setShowMin] = useState(showMinInitial)
  const [showMax, setShowMax] = useState(showMaxInitial)

  const handleNumber = (text: string): number | undefined => {
    if (typeof text === 'string' && text.trim().length === 0) {
      return undefined
    }

    const normalized = text.replace(/[^0-9.-]/g, '')
    if (normalized.trim().length === 0) {
      return undefined
    }

    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const clamp = (value: number, lower?: number, upper?: number): number => {
    let result = value
    if (typeof lower === 'number') {
      result = Math.max(result, lower)
    }
    if (typeof upper === 'number') {
      result = Math.min(result, upper)
    }
    return result
  }

  const getMinError = (): string | null => {
    if (min === undefined) {
      return null
    }

    if (typeof minProps?.min === 'number' && min < minProps.min) {
      return tValidation(($) => $.number_too_small, { min: minProps.min })
    }

    const upperBound = typeof max === 'number' ? max : maxProps?.max
    if (typeof upperBound === 'number' && min > upperBound) {
      return tValidation(($) => $.number_too_large, { max: upperBound })
    }

    return null
  }

  const getMaxError = (): string | null => {
    if (max === undefined) {
      return null
    }

    if (typeof maxProps?.max === 'number' && max > maxProps.max) {
      return tValidation(($) => $.number_too_large, { max: maxProps.max })
    }

    const lowerBound = typeof min === 'number' ? min : minProps?.min
    if (typeof lowerBound === 'number' && max < lowerBound) {
      return tValidation(($) => $.number_too_small, { min: lowerBound })
    }

    return null
  }

  return (
    <Animated.View
      key={label}
      layout={LinearTransition.duration(150)}
      className={cn('w-full flex-row items-start gap-3 sm:gap-6', className)}>
      {showMin ? (
        type === 'price' ? (
          <PriceInput
            testID={testIdBase ? `${testIdBase}-min-input` : undefined}
            value={min}
            onChange={(val) => onChange({ min: val, max })}
            onEndEditing={() => {
              if (typeof min !== 'number') {
                return
              }

              const upperBound = typeof max === 'number' ? max : maxProps?.max
              const clamped = clamp(min, minProps?.min, upperBound)
              if (clamped !== min) {
                onChange({ min: clamped, max })
              }
            }}
            placeholder={minPlaceholder}
            className={cn('min-w-0 flex-1', inputClassName)}
            inputClassName="text-right font-light text-xl"
            variant="line-dark"
            unit={unit}
            tabIndex={0}
            error={getMinError()}
          />
        ) : (
          <Input
            testID={testIdBase ? `${testIdBase}-min-input` : undefined}
            type="numeric"
            variant="line-dark"
            placeholder={minPlaceholder}
            value={min !== undefined ? String(min) : ''}
            onChangeText={(text) => {
              const next = handleNumber(text)
              const prevMin = numVal(min)

              if (next === undefined || Number.isNaN(next)) {
                onChange({ min: prevMin, max })
                return
              }

              onChange({ min: next, max })
            }}
            className={cn('min-w-0 flex-1', inputClassName)}
            inputClassName="text-center font-light text-xl"
            allowNegative={false}
            formatted={formatted}
            thousandSeparator={formatted ? ' ' : undefined}
            suffix={unit ? unit : undefined}
            prefix={minPrefix}
            textAlign="right"
            onEndEditing={() => {
              if (typeof min !== 'number') {
                return
              }

              const upperBound = typeof max === 'number' ? max : maxProps?.max
              const clamped = clamp(min, minProps?.min, upperBound)
              if (clamped !== min) {
                onChange({ min: clamped, max })
              }
            }}
            error={getMinError()}
            {...(minProps || {})}
          />
        )
      ) : (
        <Button variant="outline" className="shrink-0" onPress={() => setShowMin(true)}>
          {t(($) => $.inputs.addMin)}
        </Button>
      )}
      {showMin && showMax && <Label className="self-center">{'–'}</Label>}
      {showMax ? (
        type === 'price' ? (
          <PriceInput
            testID={testIdBase ? `${testIdBase}-max-input` : undefined}
            value={max}
            onChange={(val) => onChange({ min, max: val })}
            onEndEditing={() => {
              if (typeof max !== 'number') {
                return
              }

              const lowerBound = typeof min === 'number' ? min : minProps?.min
              const clamped = clamp(max, lowerBound, maxProps?.max)
              if (clamped !== max) {
                onChange({ min, max: clamped })
              }
            }}
            placeholder={maxPlaceholder}
            className={cn('min-w-0 flex-1', inputClassName)}
            inputClassName="text-right font-light text-xl"
            variant="line-dark"
            unit={unit}
            tabIndex={0}
            error={getMaxError()}
          />
        ) : (
          <Input
            testID={testIdBase ? `${testIdBase}-max-input` : undefined}
            type="numeric"
            variant="line-dark"
            placeholder={maxPlaceholder}
            value={max !== undefined ? String(max) : ''}
            onChangeText={(text) => {
              const next = handleNumber(text)
              const prevMax = numVal(max)

              if (next === undefined || Number.isNaN(next)) {
                onChange({ min, max: prevMax })
                return
              }
              onChange({ min, max: next })
            }}
            className={cn('min-w-0 flex-1', inputClassName)}
            inputClassName="text-center font-light text-xl"
            allowNegative={false}
            formatted={formatted}
            thousandSeparator={formatted ? ' ' : undefined}
            suffix={unit ? unit : undefined}
            prefix={maxPrefix}
            onEndEditing={() => {
              if (typeof max !== 'number') {
                return
              }

              const lowerBound = typeof min === 'number' ? min : minProps?.min
              const clamped = clamp(max, lowerBound, maxProps?.max)
              if (clamped !== max) {
                onChange({ min, max: clamped })
              }
            }}
            error={getMaxError()}
            {...(maxProps || {})}
          />
        )
      ) : (
        <Button variant="outline" className="shrink-0" onPress={() => setShowMax(true)}>
          {t(($) => $.inputs.addMax)}
        </Button>
      )}
    </Animated.View>
  )
}
